import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, of } from 'rxjs';

import { FrontendContactChannel, FrontendContactPage } from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';

const CONTACT_PATH = '/contact-us';
const DEFAULT_META_TITLE = 'Contact SavingArena | Support, Feedback & Partnerships';
const DEFAULT_META_DESCRIPTION =
  'Questions, feedback or a partnership idea? Contact the SavingArena team — we reply to every message within one business day.';

type ContactChannelIcon = 'mail' | 'phone' | 'chat';

interface ContactChannelVm {
  label: string;
  value: string;
  description: string;
  href: string;
  icon: ContactChannelIcon;
}

const DEFAULT_H1 = 'Get in touch';
const DEFAULT_SHORT_DESCRIPTION =
  "Have a question, partnership idea or feedback? Our team is here to help — reach out and we'll get back to you shortly.";
const DEFAULT_FORM_HEADING = 'Send us a message';
const DEFAULT_FORM_DESCRIPTION =
  'Fill in the form and our team will respond within one business day.';
const DEFAULT_SUCCESS_MESSAGE =
  "Thanks for reaching out! We've received your message and will get back to you within 24 hours.";
const DEFAULT_CHANNELS: FrontendContactChannel[] = [
  {
    label: 'Email us',
    value: 'help@savingarena.com',
    description: 'We reply to every message within 24 hours.',
    href: 'mailto:help@savingarena.com',
  },
  {
    label: 'Partnerships',
    value: 'partners@savingarena.com',
    description: "Stores and brands — let's grow together.",
    href: 'mailto:partners@savingarena.com',
  },
  {
    label: 'Help center',
    value: 'Browse common questions',
    description: 'Quick answers to the things shoppers ask most.',
    href: '',
  },
];

const GENERIC_SEND_ERROR =
  "We couldn't send your message just now. Please try again in a moment, or email us directly.";

/**
 * What to tell the visitor when a send fails.
 *
 * A 422 carries the server's own per-field messages (it re-checks everything the form checks, since
 * the endpoint can be posted without the page). Those are worth showing — they say which field is
 * wrong. Anything else is an outage or a network drop, where a specific-sounding message would only
 * mislead, so it gets the generic line and the offer to email instead.
 */
function sendErrorMessage(error: unknown): string {
  const response = error as { status?: number; error?: { message?: string; errors?: unknown } };
  if (response?.status !== 422) return GENERIC_SEND_ERROR;

  const errors = response.error?.errors;
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors as Record<string, unknown>).find(
      (value): value is string => typeof value === 'string' && value.trim().length > 0,
    );
    if (first) return first;
  }

  return response.error?.message?.trim() || GENERIC_SEND_ERROR;
}

function resolveChannelIcon(channel: FrontendContactChannel): ContactChannelIcon {
  const href = (channel.href ?? '').trim().toLowerCase();
  const value = (channel.value ?? '').trim().toLowerCase();
  if (href.startsWith('tel:')) {
    return 'phone';
  }
  if (href.startsWith('mailto:') || value.includes('@')) {
    return 'mail';
  }
  return 'chat';
}

@Component({
  selector: 'app-contact-page',
  imports: [Breadcrumb, ReactiveFormsModule, BigSaleBanner],
  templateUrl: './contact-page.html',
  styleUrl: './contact-page.scss',
})
export class ContactPage {
  protected readonly crumbs = breadcrumbTrail({ label: 'Contact' });

  private readonly formBuilder = inject(FormBuilder);
  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);

  private readonly h1Override = signal('');
  private readonly shortDescriptionOverride = signal('');
  private readonly formHeadingOverride = signal('');
  private readonly formDescriptionOverride = signal('');
  private readonly successMessageOverride = signal('');
  private readonly channelsOverride = signal<FrontendContactChannel[]>([]);

  protected readonly h1 = computed(() => this.h1Override() || DEFAULT_H1);
  protected readonly shortDescription = computed(
    () => this.shortDescriptionOverride() || DEFAULT_SHORT_DESCRIPTION,
  );
  protected readonly formHeading = computed(
    () => this.formHeadingOverride() || DEFAULT_FORM_HEADING,
  );
  protected readonly formDescription = computed(
    () => this.formDescriptionOverride() || DEFAULT_FORM_DESCRIPTION,
  );
  protected readonly successMessage = computed(
    () => this.successMessageOverride() || DEFAULT_SUCCESS_MESSAGE,
  );
  protected readonly channels = computed<ContactChannelVm[]>(() => {
    const source = this.channelsOverride().length > 0 ? this.channelsOverride() : DEFAULT_CHANNELS;
    return source.map((channel) => ({
      label: (channel.label ?? '').trim(),
      value: (channel.value ?? '').trim(),
      description: (channel.description ?? '').trim(),
      href: (channel.href ?? '').trim(),
      icon: resolveChannelIcon(channel),
    }));
  });

  protected readonly submitted = signal(false);
  /** True between pressing Send and the server answering — the button says so and locks. */
  protected readonly isSending = signal(false);
  /**
   * Why the last send failed, or '' when it did not.
   *
   * A message the visitor typed and lost is worse than no form at all, so a failure keeps the form
   * filled in and says what happened, rather than clearing it and claiming success — which is
   * exactly what this page did before it was wired up.
   */
  protected readonly sendError = signal('');
  protected readonly contactForm;

  constructor() {
    this.contactForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      subject: [''],
      message: ['', [Validators.required, Validators.minLength(20)]],
      // Honeypot. Hidden from people and from assistive tech; bots fill it in. Never shown, never
      // validated — the server decides what to do with a filled one.
      companyWebsite: [''],
    });

    this.api
      .getContactPageSettings()
      .pipe(
        catchError(() => of({ success: false, data: null })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.applySettings(response.data);
        // Applied even when the admin settings are missing, so the page always ships a
        // title/description/canonical rather than the app shell defaults.
        this.applySeo(response.data);
      });
  }

  private applySeo(settings: FrontendContactPage | null | undefined): void {
    const title = (settings?.metaTitle ?? '').trim() || DEFAULT_META_TITLE;
    const description = (settings?.metaDescription ?? '').trim() || DEFAULT_META_DESCRIPTION;
    const url = this.seo.absolute(CONTACT_PATH);

    this.seo.apply({ title, description, canonicalPath: CONTACT_PATH });

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  protected submitForm(): void {
    if (this.contactForm.invalid || this.isSending()) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const value = this.contactForm.getRawValue();

    this.isSending.set(true);
    this.sendError.set('');

    this.api
      .submitContactMessage({
        name: (value.name ?? '').trim(),
        email: (value.email ?? '').trim(),
        subject: (value.subject ?? '').trim(),
        message: (value.message ?? '').trim(),
        sourcePath: CONTACT_PATH,
        companyWebsite: value.companyWebsite ?? '',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSending.set(false);
          // Cleared only now that the server has it. `companyWebsite` resets to '' with everything
          // else, which is the value the honeypot is supposed to have.
          this.contactForm.reset();
          this.submitted.set(true);
        },
        error: (error: unknown) => {
          this.isSending.set(false);
          this.submitted.set(false);
          this.sendError.set(sendErrorMessage(error));
        },
      });
  }

  private applySettings(settings: FrontendContactPage | null | undefined): void {
    if (!settings) {
      return;
    }

    this.h1Override.set((settings.h1 ?? '').trim());
    this.shortDescriptionOverride.set((settings.shortDescription ?? '').trim());
    this.formHeadingOverride.set((settings.formHeading ?? '').trim());
    this.formDescriptionOverride.set((settings.formDescription ?? '').trim());
    this.successMessageOverride.set((settings.successMessage ?? '').trim());

    const channels = Array.isArray(settings.channels)
      ? settings.channels
          .map((channel) => ({
            label: (channel.label ?? '').trim(),
            value: (channel.value ?? '').trim(),
            description: (channel.description ?? '').trim(),
            href: (channel.href ?? '').trim(),
          }))
          .filter((channel) => channel.label || channel.value || channel.description)
      : [];
    this.channelsOverride.set(channels);
  }
}
